import { IAllProductBylCategoryData } from './types';
import { createSlice } from '@reduxjs/toolkit';
import { find, get } from 'lodash';
import { geneAsyncAction } from 'services/actionConfigs';
import { RootState } from 'store';
import { useAppSelector } from 'store/hooks';
import { featureName } from './constants';
import { IProductCategoryItem } from '../types/product';
import { IStaffItem } from '../types/staff';
import { ITaxSettingResData } from '../types/tax';
import { IFeeCreditCardResData } from '../types/fee';
import { ICashIncentiveResData } from '../types/incentive';
import { IShopInfoResData } from '../types/shop';
import { IPaymentOtherItem } from '../types/payment';
import { ILoyaltyProgramResData, ILoyaltyRedeem } from '../types/loyalty';
import settingGeneralService from 'modules/settings/general_configuration/services';
import { IProductVIPItemData } from '../types/vip';
const createAsync = geneAsyncAction(featureName);

const async = {
  fetchData: createAsync('fetchData'),
  getAllProductByCategory: createAsync('getAllProductByCategory'),
  getCategories: createAsync('getCategories'),
  getStaffs: createAsync('getStaffs'),
  getSettingTax: createAsync('getSettingTax'),
  getFeeCreditCard: createAsync('getFeeCreditCard'),
  getCashIncentive: createAsync('getCashIncentive'),
  getShopInfo: createAsync('getShopInfo'),
  getPaymentOthers: createAsync('getPaymentOthers'),
  getLoyaltyProgram: createAsync('getLoyaltyProgram'),
  getPackageVip: createAsync('getPackageVip'),
};

export type IshopReducerState = {
  isLoading: boolean;
  allProductByCategory: IAllProductBylCategoryData[];
  categories: IProductCategoryItem[];
  staffs: IStaffItem[];
  settingTax: ITaxSettingResData | null;
  feeCreditCard: IFeeCreditCardResData | null;
  cashIncentive: ICashIncentiveResData | null;
  shopInfo: IShopInfoResData | null;
  paymentOthers: IPaymentOtherItem[];
  loyaltyProgram: ILoyaltyProgramResData | null;
  loyaltyRedeem: ILoyaltyRedeem | null;
  packageVIP: IProductVIPItemData[];
};

type MyState = IshopReducerState;

const reducers = {
  uiInit: (state: MyState, { }: { payload: string }) => {
    state.isLoading = true;
  },
  initData: (__: MyState, { }: { payload: string }) => { },
};
// @ts-ignore
const getCurrentState = (state: RootState): MyState => state['shop']; // ex: state['booking']

const selector = <T = MyState>(key: keyof T, defaultValue?: any) =>
  useAppSelector((state) => get(getCurrentState(state), key, defaultValue));

const selectors = {
  isLoading: () => selector('isLoading') as MyState['isLoading'],
  allProductByCategory: () =>
    selector('allProductByCategory') as MyState['allProductByCategory'],
  categories: () => selector('categories') as MyState['categories'],
  staffs: () => selector('staffs') as MyState['staffs'],
  settingTax: () => selector('settingTax') as MyState['settingTax'],
  feeCreditCard: () => selector('feeCreditCard') as MyState['feeCreditCard'],
  cashIncentive: () => selector('cashIncentive') as MyState['cashIncentive'],
  shopInfo: () => selector('shopInfo') as MyState['shopInfo'],
  paymentOthers: () => selector('paymentOthers') as MyState['paymentOthers'],
  loyaltyProgram: () => selector('loyaltyProgram') as MyState['loyaltyProgram'],
  packageVIP: () => selector('packageVIP') as MyState['packageVIP'],
  loyaltyRedeem: () => selector('loyaltyRedeem') as MyState['loyaltyRedeem'],
  getCategories: (state: RootState) => getCurrentState(state).categories
};

const Slice = createSlice({
  name: featureName,
  initialState: {
    isLoading: false,
    categories: [],
    allProductByCategory: [],
    staffs: [],
    settingTax: null,
    feeCreditCard: null,
    cashIncentive: null,
    shopInfo: null,
    paymentOthers: [],
    loyaltyProgram: null,
    loyaltyRedeem: null,
    packageVIP: [],
  } as MyState,
  reducers,
  extraReducers: (builder) => {
    builder
      .addCase(async.getLoyaltyProgram.fetch, (state) => {
        state.loyaltyProgram = null;
        state.loyaltyRedeem = null;
      })
      .addCase(async.getLoyaltyProgram.success, (state, { payload }) => {
        state.loyaltyProgram = payload;
        state.loyaltyRedeem = find(payload, o => o.type === 'LOYALTY_REDEMPTION') || null;
        if (state.loyaltyRedeem) {
          state.loyaltyRedeem.oneDollarToPoint = state.loyaltyRedeem.point / state.loyaltyRedeem.amount;
        }
      })
      .addCase(async.getLoyaltyProgram.fail, (state) => {
        state.loyaltyProgram = null;
      })

      .addCase(async.getPackageVip.fetch, (state) => {
        state.packageVIP = [];
      })
      .addCase(async.getPackageVip.success, (state, { payload }) => {
        state.packageVIP = payload;
      })
      .addCase(async.getPackageVip.fail, (state) => {
        state.packageVIP = [];
      })

      .addCase(async.getPaymentOthers.fetch, (state) => {
        state.paymentOthers = [];
      })
      .addCase(async.getPaymentOthers.success, (state, { payload }) => {
        state.paymentOthers = payload;
      })
      .addCase(async.getPaymentOthers.fail, (state) => {
        state.paymentOthers = [];
      })

      .addCase(async.getShopInfo.fetch, (state) => {
        state.shopInfo = null;
      })
      .addCase(async.getShopInfo.success, (state, { payload }) => {
        state.shopInfo = payload;
      })
      .addCase(async.getShopInfo.fail, (state) => {
        state.shopInfo = null;
      })

      .addCase(async.getCashIncentive.fetch, (state) => {
        state.cashIncentive = null;
      })
      .addCase(settingGeneralService.slice.actions.api.discount.get.success, (state, { payload }) => {
        state.cashIncentive = payload;
      })
      .addCase(async.getCashIncentive.success, (state, { payload }) => {
        state.cashIncentive = payload;
      })
      .addCase(async.getCashIncentive.fail, (state) => {
        state.cashIncentive = null;
      })

      .addCase(async.getFeeCreditCard.fetch, (state) => {
        state.feeCreditCard = null;
      })
      .addCase(settingGeneralService.slice.actions.api.feeCreditCard.get.success, (state, { payload }) => {
        state.feeCreditCard = payload;
      })
      .addCase(async.getFeeCreditCard.success, (state, { payload }) => {
        state.feeCreditCard = payload;
      })
      .addCase(async.getFeeCreditCard.fail, (state) => {
        state.feeCreditCard = null;
      })

      .addCase(async.getSettingTax.fetch, (state) => {
        state.settingTax = null;
      })
      .addCase(settingGeneralService.slice.actions.api.tax.get.success, (state, { payload }) => {
        state.settingTax = payload;
      })
      .addCase(async.getSettingTax.success, (state, { payload }) => {
        state.settingTax = payload;
      })
      .addCase(async.getSettingTax.fail, (state) => {
        state.settingTax = null;
      })

      .addCase(async.getStaffs.fetch, (state) => {
        state.staffs = [];
      })
      .addCase(async.getStaffs.success, (state, { payload }) => {
        state.staffs = payload;
      })
      .addCase(async.getStaffs.fail, (state) => {
        state.staffs = [];
      })

      .addCase(async.getCategories.fetch, (state) => {
        state.categories = [];
      })
      .addCase(async.getCategories.success, (state, { payload }) => {
        state.categories = payload;
      })
      .addCase(async.getCategories.fail, (state) => {
        state.categories = [];
      })

      .addCase(async.fetchData.fetch, (state) => {
        state.isLoading = true;
      })
      .addCase(async.fetchData.success, (state) => {
        state.isLoading = false;
      })
      .addCase(async.fetchData.fail, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(async.getAllProductByCategory.fetch, (state) => {
        state.allProductByCategory = [];
      })
      .addCase(async.getAllProductByCategory.success, (state, { payload }) => {
        state.allProductByCategory = payload;
      })
      .addCase(async.getAllProductByCategory.fail, (state) => {
        state.allProductByCategory = [];
      });
  },
});

const actions = {
  ...Slice.actions,
  async,
};
const reducer = Slice.reducer;

export { actions, reducer, selectors };
